import React, { useState, useEffect } from 'react';
import { 
  LaneFormData
} from '../../types';
import { useEnhancedApi } from '../../hooks/useEnhancedApi';
import { LaneAssignment } from '../../services/laneAssignmentService';
import { useAppStore } from '../../store/app';
import { useToast } from '../ui/Toast';
import { permissionChecks } from '../../lib/auth/helpers';

interface LaneAssignmentFormData {
  terminal_id: number;
  checkpoint_id: number;
  lane_type_id: number;
  description: string;
}

export const LanesManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<LaneAssignment | null>(null);
  const [laneAssignments, setLaneAssignments] = useState<LaneAssignment[]>([]);
  
  // Local state for dropdowns as fallback
  const [localTerminals, setLocalTerminals] = useState<any[]>([]);
  const [localCheckpoints, setLocalCheckpoints] = useState<any[]>([]);
  const [localLaneTypes, setLocalLaneTypes] = useState<any[]>([]);
  
  // Use authenticated API service
  const enhancedApi = useEnhancedApi();
  
  // Store hooks with safe array extraction
  const storeData = useAppStore();
  const {
    fetchTerminals,
    fetchCheckpoints,
    fetchLaneTypes
  } = storeData;

  // Ensure all arrays are properly initialized
  const terminals = Array.isArray(storeData.terminals) ? storeData.terminals : [];
  const checkpoints = Array.isArray(storeData.checkpoints) ? storeData.checkpoints : [];
  const laneTypes = Array.isArray(storeData.laneTypes) ? storeData.laneTypes : [];
  
  // Use local data as fallback if store data is empty
  const availableTerminals = terminals.length > 0 ? terminals : localTerminals;
  const availableCheckpoints = checkpoints.length > 0 ? checkpoints : localCheckpoints;
  const availableLaneTypes = laneTypes.length > 0 ? laneTypes : localLaneTypes;
  
  const { showToast } = useToast();

  // Form data state
  const [formData, setFormData] = useState<LaneAssignmentFormData>({
    terminal_id: 0,
    checkpoint_id: 0,
    lane_type_id: 0,
    description: ''
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTerminals(),
        fetchCheckpoints(),
        fetchLaneTypes(),
        loadLaneAssignments()
      ]);
      
      // Also try direct API calls and store locally
      try {
        const directTerminals = await enhancedApi.getTerminals();
        const directCheckpoints = await enhancedApi.getCheckpoints();
        const directLaneTypes = await enhancedApi.getLaneTypes();
        
        // Extract data from API response structure
        const terminalsData = (directTerminals as any)?.data || directTerminals || [];
        const checkpointsData = (directCheckpoints as any)?.data || directCheckpoints || [];
        const laneTypesData = (directLaneTypes as any)?.data || directLaneTypes || [];
        
        // Store in local state as fallback
        setLocalTerminals(Array.isArray(terminalsData) ? terminalsData : []);
        setLocalCheckpoints(Array.isArray(checkpointsData) ? checkpointsData : []);
        setLocalLaneTypes(Array.isArray(laneTypesData) ? laneTypesData : []);
        
            // LanesManagement - data loaded
        
      } catch (directError) {
        console.error('Direct API calls failed:', directError);
      }
      
    } catch (error) {
      console.error('Failed to load initial data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadLaneAssignments = async () => {
    try {
      const assignments = await enhancedApi.getLaneAssignments();
      setLaneAssignments(assignments);
    } catch (error) {
      console.error('Failed to load lane assignments:', error);
      showToast('Failed to load lane assignments', 'error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    const numericFields = ['terminal_id', 'checkpoint_id', 'lane_type_id'];
    setFormData(prev => ({ 
      ...prev, 
      [name]: numericFields.includes(name) ? parseInt(value) || 0 : value 
    }));
    
    // Clear errors when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Reset dependent fields when parent changes
    if (name === 'terminal_id') {
      setFormData(prev => ({ ...prev, checkpoint_id: 0, lane_type_id: 0 }));
    } else if (name === 'checkpoint_id') {
      setFormData(prev => ({ ...prev, lane_type_id: 0 }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.terminal_id) {
      errors.terminal_id = 'Please select a terminal';
    }
    
    if (!formData.checkpoint_id) {
      errors.checkpoint_id = 'Please select a checkpoint';
    }
    
    if (!formData.lane_type_id) {
      errors.lane_type_id = 'Please select a lane type';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permissionChecks.canUpdateQueueAssignments()) {
      showToast('You do not have permission to create or update lane assignments', 'error');
      return;
    }
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (editingItem) {
        // Update existing lane assignment
        await enhancedApi.updateLaneAssignment(editingItem.laneassignmentID, {
          curre_lanetypeID: formData.lane_type_id,
          description: 'Updated via admin panel'
        });
        showToast('Lane assignment updated successfully!', 'success');
      } else {
        // Create new lane assignment
        await enhancedApi.createLaneAssignment({
          checkPointID: formData.checkpoint_id,
          curre_lanetypeID: formData.lane_type_id,
          description: formData.description.trim() || undefined
        });
        showToast('Lane assignment created successfully!', 'success');
      }
      
      // Reload the data
      await loadLaneAssignments();
      resetForm();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save lane assignment';
      setFormErrors({ submit: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (assignment: LaneAssignment) => {
    if (!permissionChecks.canUpdateQueueAssignments()) {
      showToast('You do not have permission to edit lane assignments', 'error');
      return;
    }
    setEditingItem(assignment);
    
    // Find the terminal for this checkpoint
    const checkpoint = checkpoints.find(c => c.checkpoint_id === assignment.checkPointID);
    const terminalId = checkpoint?.terminal_id || 0;
    
    setFormData({
      terminal_id: terminalId,
      checkpoint_id: assignment.checkPointID,
      lane_type_id: assignment.curre_lanetypeID,
      description: assignment.description || ''
    });
    
    setShowAddForm(true);
    setFormErrors({});
  };

  const resetForm = () => {
    setFormData({
      terminal_id: 0,
      checkpoint_id: 0,
      lane_type_id: 0,
      description: ''
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingItem(null);
  };

  // Helper functions
  const getTerminalName = (terminalId: number) => {
    return terminals.find(t => t.terminal_id === terminalId)?.terminal_name || 'Unknown';
  };

  const getCheckpointName = (checkpointId: number) => {
    return checkpoints.find(c => c.checkpoint_id === checkpointId)?.checkpoint_name || 'Unknown';
  };

  const getLaneTypeName = (laneTypeId: number) => {
    return laneTypes.find(lt => lt.lane_type_id === laneTypeId)?.type_name || 'Unknown';
  };

  // Filter checkpoints based on selected terminal
  const filteredCheckpoints = availableCheckpoints.filter(c => 
    formData.terminal_id ? c.terminal_id === formData.terminal_id && c.is_active : c.is_active
  );

  // Filter lane types based on selected checkpoint and purpose=APP
  const filteredLaneTypes = availableLaneTypes.filter(lt => 
    formData.checkpoint_id ? 
      lt.checkpoint_id === formData.checkpoint_id && 
      lt.is_active && 
      lt.purpose === 'APP' 
    : 
      lt.is_active && lt.purpose === 'APP'
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Queue Assignment</h1>
          <p className="text-gray-400 text-sm">Manage queue assignments and configurations</p>
        </div>
      </div>

      {/* Add Lane Assignment Button */}
      <div className="flex justify-end">
        {permissionChecks.canUpdateQueueAssignments() && (
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm font-medium"
          >
            {showAddForm ? 'Cancel' : 'Add Lane Assignment'}
          </button>
        )}
      </div>

      {/* Add/Edit Lane Assignment Form */}
      {showAddForm && (
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? 'Edit Lane Assignment' : 'Add New Lane Assignment'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Terminal Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Terminal <span className="text-red-400">*</span>
              </label>
              <select
                name="terminal_id"
                value={formData.terminal_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.terminal_id ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading}
              >
                <option value={0}>Select a terminal</option>
                {availableTerminals.filter(t => t.is_active).map(terminal => (
                  <option key={terminal.terminal_id} value={terminal.terminal_id}>
                    {terminal.terminal_name}
                  </option>
                ))}
              </select>
              {formErrors.terminal_id && (
                <p className="mt-1 text-sm text-red-400">{formErrors.terminal_id}</p>
              )}
            </div>

            {/* Checkpoint Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Checkpoint <span className="text-red-400">*</span>
              </label>
              <select
                name="checkpoint_id"
                value={formData.checkpoint_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.checkpoint_id ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading || !formData.terminal_id}
              >
                <option value={0}>Select a checkpoint</option>
                {filteredCheckpoints.map(checkpoint => (
                  <option key={checkpoint.checkpoint_id} value={checkpoint.checkpoint_id}>
                    {checkpoint.checkpoint_name}
                  </option>
                ))}
              </select>
              {formErrors.checkpoint_id && (
                <p className="mt-1 text-sm text-red-400">{formErrors.checkpoint_id}</p>
              )}
            </div>

            {/* Lane Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Lane Type <span className="text-red-400">*</span>
              </label>
              <select
                name="lane_type_id"
                value={formData.lane_type_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.lane_type_id ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading || !formData.checkpoint_id}
              >
                <option value={0}>Select a lane type</option>
                {filteredLaneTypes.map(laneType => (
                  <option key={laneType.lane_type_id} value={laneType.lane_type_id}>
                    {laneType.type_name}
                  </option>
                ))}
              </select>
              {formErrors.lane_type_id && (
                <p className="mt-1 text-sm text-red-400">{formErrors.lane_type_id}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                placeholder="Enter lane assignment description (optional)"
                disabled={loading}
              />
            </div>

            {/* Submit Error */}
            {formErrors.submit && (
              <div className="p-3 bg-red-900/50 border border-red-500 rounded-md">
                <p className="text-sm text-red-400">{formErrors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                {loading ? (editingItem ? 'Updating...' : 'Creating...') : (editingItem ? 'Update Lane Assignment' : 'Create Lane Assignment')}
              </button>
              <button 
                type="button" 
                onClick={resetForm}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lane Assignments List */}
      <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4">Queue Assignment List</h3>
        {loading && !showAddForm ? (
          <div className="text-center py-8">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p className="text-gray-400">Loading queue assignments...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {laneAssignments.filter(assignment => assignment.is_active).map((assignment) => (
              <div key={assignment.laneassignmentID} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-700 rounded space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <div className="font-medium text-white">Assignment #{assignment.laneassignmentID}</div>
                  <div className="text-sm text-gray-400 mt-1">
                    Terminal: {getTerminalName(checkpoints.find(c => c.checkpoint_id === assignment.checkPointID)?.terminal_id || 0)}
                  </div>
                  <div className="text-sm text-gray-400">
                    Checkpoint: {assignment.checkpoint_name}
                  </div>
                  <div className="text-sm text-gray-400">
                    Lane Type: {assignment.current_lanetype_name}
                  </div>
                  {assignment.description && (
                    <div className="text-sm text-gray-400 mt-1">
                      Description: {assignment.description}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Updated: {new Date(assignment.last_updated).toLocaleDateString()} by {assignment.updated_by}
                  </div>
                </div>
                {permissionChecks.canUpdateQueueAssignments() ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Edit Button */}
                    <button 
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                      onClick={() => handleEdit(assignment)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {laneAssignments.filter(assignment => assignment.is_active).length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400">
                No lane assignments found. Create your first lane assignment to get started.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};